import { ReactElement, ReactNode, useMemo } from 'react';
import styles from './device-features-icons.module.scss';
import { UIIcon, UIPopover, UIText } from '@/ui';
import fallIcon from '../assets/images/fall.png';
import tiltIcon from '../assets/images/tilt.png';
import geofenceIcon from '../assets/images/geofence.png';
import {
    Device,
    DeviceCommand,
    DeviceCommandDataModel,
    DeviceConfigGeoAlert,
    DeviceConfigWifi
} from '@/api';
import { charcoal } from '@/ui/styles';
import { useDevicesTranslation } from '../hooks';
import { deriveDeviceFeatureStates } from '../util/device-feature-states';
import { FeatureIconWithDot } from './feature-icon-with-dot';
import { PendingBadge } from './pending-badge';
import { StatusCell } from './pending-cells';

type DeviceFeaturesIconsProps = {
    device: Device;
};
export function DeviceFeaturesIcons(
    props: DeviceFeaturesIconsProps
): ReactElement {
    const { device } = props;
    const { t } = useDevicesTranslation();
    const fallAlertsString = t('components.device_features_icons.fall_alt');
    const tiltAlertsString = t('components.device_features_icons.tilt_alt');
    const geogenceAlertsString = t(
        'components.device_features_icons.geofence_alt'
    );
    const wifiString = t('components.device_features_icons.wifi_alt');

    // Derive the true enabled state from the reported config, folding in any
    // pending commands, rather than the (stale) device.features flags.
    const states = useMemo(() => deriveDeviceFeatureStates(device), [device]);

    // A feature shows an icon when it is on now or will be on after a pending
    // change. This keeps a pending enable and an in-flight disable (still on
    // until the device confirms) visible, but hides a feature that is off and
    // staying off — e.g. a pending disable on an already-disabled feature.
    const showFall = states.fall.reportedEnabled || states.fall.enabled;
    const showTilt = states.tilt.reportedEnabled || states.tilt.enabled;
    const showGeofence =
        states.geofence.reportedEnabled || states.geofence.enabled;
    const showWifi = states.wifi.reportedEnabled || states.wifi.enabled;

    const hasFeatures = showFall || showTilt || showGeofence || showWifi;

    if (!hasFeatures) {
        return (
            <UIText type="default2">
                {t('components.device_features_icons.none')}
            </UIText>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2
            }}
        >
            {showFall && (
                <FeatureIcon
                    device={device}
                    feature="fall"
                    pending={states.fall.pending}
                >
                    <img
                        src={fallIcon}
                        alt={fallAlertsString}
                        className={styles.featureIcon}
                    />
                </FeatureIcon>
            )}

            {showTilt && (
                <FeatureIcon
                    device={device}
                    feature="tilt"
                    pending={states.tilt.pending}
                >
                    <img
                        src={tiltIcon}
                        alt={tiltAlertsString}
                        className={styles.featureIcon}
                    />
                </FeatureIcon>
            )}

            {showGeofence && (
                <FeatureIcon
                    device={device}
                    feature="geofence"
                    pending={states.geofence.pending}
                >
                    <img
                        src={geofenceIcon}
                        alt={geogenceAlertsString}
                        className={styles.featureIcon}
                    />
                </FeatureIcon>
            )}

            {showWifi && (
                <FeatureIcon
                    device={device}
                    feature="wifi"
                    pending={states.wifi.pending}
                >
                    <div className={styles.featureIcon} aria-label={wifiString}>
                        <UIIcon name="wifi" size={16} color={charcoal} />
                    </div>
                </FeatureIcon>
            )}
        </div>
    );
}

/**
 * Wraps a feature icon, adding an amber dot when a change is pending and a hover
 * popover showing the feature's current and pending config.
 */
function FeatureIcon(props: {
    device: Device;
    feature: DeviceFeature;
    pending: boolean;
    children: ReactNode;
}): ReactElement {
    const { device, feature, pending, children } = props;

    return (
        <UIPopover
            trigger="hover"
            placement="bottom_left"
            popoverPadding={4}
            triggerNode={
                <button type="button" className={styles.featureIconButton}>
                    <FeatureIconWithDot pending={pending}>
                        {children}
                    </FeatureIconWithDot>
                </button>
            }
        >
            <DeviceFeaturePopover device={device} feature={feature} />
        </UIPopover>
    );
}

export type DeviceFeature = 'fall' | 'tilt' | 'geofence' | 'wifi';

/** A minimal translate signature so the summary builders stay pure helpers. */
type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** One config value, labelled for fall/tilt and bare for geofence/Wi-Fi. */
type Part = { label?: string; value: string };

type StatusInfo = { enabled: boolean; status: string };

/** A single row of the popover: a feature (fall/tilt) or one slot (geo/wifi). */
type Entry = {
    /** Slot label for multi-slot features; omitted for fall/tilt. */
    label?: string;
    current: StatusInfo;
    pending?: StatusInfo;
    currentParts: Part[];
    /** null when there is no pending change; [] when the pending change is off. */
    pendingParts: Part[] | null;
};

type FeatureView = {
    title: string;
    entries: Entry[];
};

/**
 * Hover content for a feature icon. Mirrors the device page's pending language:
 * an amber "Pending" badge in the header, colour-coded status, and "old → new"
 * arrows on whichever values are changing — so a user can see exactly what is
 * changing and in which direction.
 */
function DeviceFeaturePopover(props: {
    device: Device;
    feature: DeviceFeature;
}): ReactElement {
    const { device, feature } = props;
    const { t } = useDevicesTranslation();
    const view = buildView(device, feature, t);
    const hasPending = view.entries.some((e) => e.pending !== undefined);

    return (
        <div className={styles.popover}>
            <div className={styles.popoverHeader}>
                <UIText variant="basic" bold>
                    {view.title}
                </UIText>
                {hasPending && (
                    <PendingBadge label={t('shared.labels.pending')} />
                )}
            </div>
            {view.entries.map((entry, index) => (
                <FeatureEntry key={index} entry={entry} />
            ))}
        </div>
    );
}

function FeatureEntry(props: { entry: Entry }): ReactElement {
    const { entry } = props;
    const { t } = useDevicesTranslation();
    const detail = detailString(entry, t);
    return (
        <div className={styles.popoverEntry}>
            <div className={styles.popoverEntryStatus}>
                {entry.label && (
                    <UIText variant="small" medium type="default2">
                        {entry.label}
                    </UIText>
                )}
                <StatusCell
                    small
                    current={entry.current}
                    pending={entry.pending}
                />
            </div>
            {detail && (
                <UIText variant="small" type="default2">
                    {detail}
                </UIText>
            )}
        </div>
    );
}

function partText(part: Part, t: Translate, to?: string): string {
    const changed = to !== undefined && to !== part.value;
    if (part.label) {
        return changed
            ? t('shared.devices.field_value_change', {
                  label: part.label,
                  value: part.value,
                  to
              })
            : t('shared.devices.field_value', {
                  label: part.label,
                  value: part.value
              });
    }
    return changed
        ? t('shared.devices.value_change', { value: part.value, to })
        : part.value;
}

function joinParts(parts: Part[], t: Translate): string {
    return parts
        .map((p) => partText(p, t))
        .join(t('shared.devices.detail_separator'));
}

/**
 * The detail line for an entry, applying the same rules as the device page: diff
 * per part when both sides are enabled, otherwise show the single relevant side
 * (the new config when enabling, the outgoing config when disabling).
 */
function detailString(entry: Entry, t: Translate): string {
    const { current, pending, currentParts, pendingParts } = entry;
    if (!pending || pendingParts === null) {
        return joinParts(currentParts, t);
    }
    if (current.enabled && pending.enabled) {
        return currentParts
            .map((p, i) => partText(p, t, pendingParts[i]?.value))
            .join(t('shared.devices.detail_separator'));
    }
    return pending.enabled
        ? joinParts(pendingParts, t)
        : joinParts(currentParts, t);
}

function statusInfo(enabled: boolean, t: Translate): StatusInfo {
    return {
        enabled,
        status: enabled
            ? t('shared.labels.enabled')
            : t('shared.labels.disabled')
    };
}

function buildView(
    device: Device,
    feature: DeviceFeature,
    t: Translate
): FeatureView {
    const config = device.config;
    const pending = (device.commands ?? []).filter(
        (c) => c.status === 'pending'
    );

    switch (feature) {
        case 'fall':
            return {
                title: t('shared.devices.fall_label'),
                entries: [
                    fallEntry(
                        config?.fallAlert,
                        pending.find((c) => c.type === 'set_fall_alert')?.data,
                        t
                    )
                ]
            };
        case 'tilt':
            return {
                title: t('shared.devices.tilt_label'),
                entries: [
                    tiltEntry(
                        config?.tiltAlert,
                        pending.find((c) => c.type === 'set_tilt_alert')?.data,
                        t
                    )
                ]
            };
        case 'geofence':
            return {
                title: t('shared.devices.geofence_label'),
                entries: geofenceEntries(
                    config?.geoAlert,
                    pending.filter((c) => c.type === 'set_geo_alert'),
                    t
                )
            };
        case 'wifi':
            return {
                title: t('shared.devices.wifi_label'),
                entries: wifiEntries(
                    config?.wifi,
                    pending.filter((c) => c.type === 'set_wifi'),
                    t
                )
            };
    }
}

function fallEntry(
    fall: { enabled: boolean; sensitivity: number } | null | undefined,
    cmd: DeviceCommandDataModel | undefined,
    t: Translate
): Entry {
    const current = statusInfo(fall?.enabled ?? false, t);
    const currentParts = fallParts(
        fall?.enabled ?? false,
        fall?.sensitivity,
        t
    );
    if (!cmd) {
        return { current, currentParts, pendingParts: null };
    }
    const enabled = cmd.fallAlertEnabled ?? false;
    return {
        current,
        pending: statusInfo(enabled, t),
        currentParts,
        pendingParts: fallParts(enabled, cmd.fallAlertSensitivity, t)
    };
}

function fallParts(
    enabled: boolean,
    sensitivity: number | undefined,
    t: Translate
): Part[] {
    if (!enabled || sensitivity === undefined) return [];
    return [
        {
            label: t('shared.devices.fall_sensitivity_label'),
            value: String(sensitivity)
        }
    ];
}

function tiltEntry(
    tilt:
        | { enabled: boolean; angle: number; timeThreshold: number }
        | null
        | undefined,
    cmd: DeviceCommandDataModel | undefined,
    t: Translate
): Entry {
    const current = statusInfo(tilt?.enabled ?? false, t);
    const currentParts = tiltParts(
        tilt?.enabled ?? false,
        tilt?.angle,
        tilt?.timeThreshold,
        t
    );
    if (!cmd) {
        return { current, currentParts, pendingParts: null };
    }
    const enabled = cmd.tiltAlertEnabled ?? false;
    return {
        current,
        pending: statusInfo(enabled, t),
        currentParts,
        pendingParts: tiltParts(
            enabled,
            cmd.tiltAlertAngle,
            cmd.tiltAlertTimeThreshold,
            t
        )
    };
}

function tiltParts(
    enabled: boolean,
    angle: number | undefined,
    time: number | undefined,
    t: Translate
): Part[] {
    if (!enabled || angle === undefined || time === undefined) return [];
    return [
        {
            label: t('shared.devices.tilt_angle_label'),
            value: t('shared.devices.tilt_angle_value', { value: angle })
        },
        {
            label: t('shared.devices.tilt_time_label'),
            value: t('shared.devices.tilt_time_value', { value: time })
        }
    ];
}

function geofenceEntries(
    geoAlert: Record<string, DeviceConfigGeoAlert> | undefined,
    cmds: DeviceCommand[],
    t: Translate
): Entry[] {
    const indices = slotIndices(
        geoAlert,
        cmds.map((c) => c.data.geoAlertIndex ?? 0)
    );

    return indices.map((index) => {
        const slot = geoAlert?.[String(index)];
        const cmd = cmds.find((c) => (c.data.geoAlertIndex ?? 0) === index);
        const curEnabled = slot?.enabled ?? false;

        const entry: Entry = {
            label: t('shared.devices.geofence_slot_label', {
                number: index + 1
            }),
            current: statusInfo(curEnabled, t),
            currentParts:
                curEnabled && slot
                    ? geoParts(
                          slot.shape,
                          slot.direction,
                          slot.radius,
                          slot.points?.length,
                          t
                      )
                    : [],
            pendingParts: null
        };

        if (cmd) {
            const enabled = cmd.data.geoAlertEnabled ?? false;
            entry.pending = statusInfo(enabled, t);
            entry.pendingParts = enabled
                ? geoParts(
                      cmd.data.geoAlertShape ?? null,
                      cmd.data.geoAlertDirection === 'out' ? 'out' : 'in',
                      cmd.data.geoAlertRadius ?? null,
                      cmd.data.geoAlertPoints?.length,
                      t
                  )
                : [];
        }
        return entry;
    });
}

function geoParts(
    shape: string | null,
    direction: 'in' | 'out',
    radius: number | null | undefined,
    pointCount: number | undefined,
    t: Translate
): Part[] {
    const parts: Part[] = [];
    if (shape === 'circle') {
        parts.push({ value: t('shared.devices.shape_circle') });
    } else if (shape === 'polygon') {
        parts.push({ value: t('shared.devices.shape_polygon') });
    }
    parts.push({
        value:
            direction === 'out'
                ? t('shared.devices.direction_out')
                : t('shared.devices.direction_in')
    });
    if (shape === 'circle' && radius != null) {
        parts.push({
            value: t('shared.devices.radius_value', { value: radius })
        });
    } else if (shape === 'polygon' && pointCount != null) {
        parts.push({
            value: t('shared.devices.points_value', { count: pointCount })
        });
    }
    return parts;
}

function wifiEntries(
    wifi: Record<string, DeviceConfigWifi> | undefined,
    cmds: DeviceCommand[],
    t: Translate
): Entry[] {
    const indices = slotIndices(
        wifi,
        cmds.map((c) => c.data.wifiIndex ?? 0)
    );

    return indices.map((index) => {
        const slot = wifi?.[String(index)];
        const cmd = cmds.find((c) => (c.data.wifiIndex ?? 0) === index);
        const curEnabled = slot?.enabled ?? false;

        const entry: Entry = {
            label: t('shared.devices.wifi_slot_label', { number: index + 1 }),
            current: statusInfo(curEnabled, t),
            currentParts:
                curEnabled && slot?.ssid ? [{ value: slot.ssid }] : [],
            pendingParts: null
        };

        if (cmd) {
            const enabled = cmd.data.wifiEnabled ?? false;
            entry.pending = statusInfo(enabled, t);
            entry.pendingParts =
                enabled && cmd.data.wifiSsid
                    ? [{ value: cmd.data.wifiSsid }]
                    : [];
        }
        return entry;
    });
}

/** Sorted, de-duplicated slot indices that are currently enabled or pending. */
function slotIndices(
    config: Record<string, { enabled: boolean }> | undefined,
    pendingIndices: number[]
): number[] {
    const indices = new Set<number>(pendingIndices);
    Object.entries(config ?? {}).forEach(([index, slot]) => {
        if (slot.enabled) indices.add(Number(index));
    });
    return [...indices].sort((a, b) => a - b);
}
